import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { getBoardSection, getBoardMembers } from '../lib/api';
import { BrandMark, EmptyState, LoadingScreen, PageHero, staggerContainer, staggerItem } from '../components/common/BrandElements';

const BoardPage = () => {
  const { t } = useLanguage();
  const [section, setSection] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [sectionRes, membersRes] = await Promise.all([getBoardSection(), getBoardMembers()]);
        setSection(sectionRes.data);
        setMembers(membersRes.data.filter(m => m.is_active));
      } catch (error) {
        console.error('Error loading board:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-raad-porcelain" data-testid="board-page">
      <PageHero
        kicker={t('القيادة', 'Leadership')}
        title={t('مجلس الإدارة', 'Board of Directors')}
        description={section ? t(section.intro_ar, section.intro_en) : t('فريق قيادي يوازن بين الثبات والرؤية المستقبلية.', 'A leadership team balancing stability and future vision.')}
      />

      <section className="pb-24">
        <div className="container mx-auto px-6">
          {members.length === 0 ? (
            <EmptyState title={t('لا يوجد أعضاء حالياً', 'No members available')} />
          ) : (
            <motion.div variants={staggerContainer} initial="hidden" animate="show" className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {members.map((member) => (
                <motion.article key={member.id} variants={staggerItem} className="group relative overflow-hidden rounded-[2rem] border border-raad-teal/15 bg-white p-6 text-center shadow-soft transition-all hover:-translate-y-1 hover:shadow-teal">
                  <div className="absolute end-0 top-0 h-8 w-44 bg-raad-teal/20 brand-slash-end" />
                  <div className="relative mx-auto mb-6 h-56 w-56 overflow-hidden rounded-[2rem] bg-raad-mist">
                    <img
                      src={member.image_url || '/assets/raad-identity-hero.png'}
                      alt={t(member.name_ar, member.name_en)}
                      className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0"
                    />
                    <div className="absolute bottom-3 start-3 rounded-full bg-white p-2 shadow-soft">
                      <BrandMark className="h-7 w-7" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-black text-raad-charcoal">
                    {t(member.name_ar, member.name_en)}
                  </h3>
                  {(member.position_ar || member.position_en) && (
                    <p className="mt-2 text-sm font-black uppercase tracking-[0.16em] text-raad-teal">
                      {t(member.position_ar, member.position_en)}
                    </p>
                  )}
                  <p className="mt-4 text-sm leading-7 text-raad-graphite/65">
                    {t(member.bio_ar, member.bio_en)}
                  </p>
                </motion.article>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BoardPage;
